import { useCallback, useEffect, useState } from 'react'
import { Search, RefreshCcw, Download, ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react'
import * as XLSX from 'xlsx'
import Header from '@/components/layout/Header'
import MemberCard, { type Member } from '@/components/members/MemberCard'
import MemberManageDialog from '@/components/members/MemberManageDialog'
import WdlRosterPanel from '@/components/members/WdlRosterPanel'
import Panel, { PanelEmpty, panelCard, panelGhostButton, panelIconButton, panelInput } from '@/components/layout/Panel'
import { Button } from '@/components/ui/button'
import { useSearchParams } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import SteamIcon from '@/components/site/SteamIcon'
import SteamLinkBanner from '@/components/SteamLinkBanner'
import { memberSteamProfileUrl } from '@/lib/steam'
import { useAuth, isAdmin, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

// Sıralama ölçütleri. Her birinin doğal yönü var (ad A→Z, gerisi en çok / en
// yeni önce); yön düğmesi onu çevirir. Değeri bilinmeyen üye (gizli profil,
// bağlanmamış Steam, katılımı kayıtsız) yönden bağımsız hep sonda kalır: "en
// az oynayan" sıralamasının başını Steam'i okunamayanlar doldurmamalı.
const SORT_KEYS = ['name', 'lastSeen', 'playtimeTotal', 'playtimeFortnight', 'clanJoined'] as const
type SortKey = typeof SORT_KEYS[number]
type SortState = { key: SortKey; desc: boolean }

const SORT_STORAGE_KEY = 'tll_members_sort'
const NATURAL_DESC: Record<SortKey, boolean> = {
  name: false, lastSeen: true, playtimeTotal: true, playtimeFortnight: true, clanJoined: true,
}

function loadSort(): SortState {
  try {
    const raw = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) ?? 'null')
    if (raw && SORT_KEYS.includes(raw.key)) return { key: raw.key, desc: !!raw.desc }
  } catch { /* yok ya da okunamıyor: varsayılana düş */ }
  return { key: 'name', desc: false }
}

export default function Members() {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const canEdit = isOperator(user)
  // Roles are granted in Discord now; admins can still see who holds what.
  const canSeeRoles = isAdmin(user)
  // SteamID64s are the staff's working data — the roster itself only names the
  // linked account. The backend leaves the ids out for everyone else, so this
  // only decides whether the line is drawn, never whether the data is there.
  const canSeeSteamIds = isOperator(user)

  const [members, setMembers] = useState<Member[]>([])
  const [lastSeen, setLastSeen] = useState<Record<string, number>>({})
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set())
  const [roles, setRoles] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sort, setSortState] = useState<SortState>(loadSort)
  const setSort = (next: SortState) => {
    setSortState(next)
    try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(next)) } catch { /* yalnızca bu oturum */ }
  }
  const [managingId, setManagingId] = useState<string | null>(null)
  const managing = members.find(m => m.id === managingId) ?? null

  const patchMember = (id: string, patch: Partial<Member>) =>
    setMembers(prev => prev.map(m => (m.id === id ? { ...m, ...patch } : m)))

  // Steam bounces back to /members?steam=linked|failed|taken. Show the outcome
  // once, then drop the parameter so a refresh does not replay a stale message.
  const [params, setParams] = useSearchParams()
  const steamResult = params.get('steam')
  const [steamMsg, setSteamMsg] = useState('')
  useEffect(() => {
    if (!steamResult) return
    setSteamMsg(
      steamResult === 'linked' ? T.members.steamLinkedToast
      : steamResult === 'taken' ? T.members.steamTakenToast
      : T.members.steamFailedToast
    )
    const next = new URLSearchParams(params)
    next.delete('steam')
    setParams(next, { replace: true })
  }, [steamResult, params, setParams, T])

  const me = members.find(m => m.id === user?.id)

  async function unlinkOwnSteam() {
    await fetch('/api/steam', { method: 'DELETE' })
    void fetchMembers(true)
  }

  const fetchMembers = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    try {
      const url = force ? '/api/members?refresh=true' : '/api/members'
      const reqs: Promise<Response>[] = [
        fetch(url),
        fetch('/api/online/all'),
        fetch('/api/online'),
      ]
      if (canSeeRoles) reqs.push(fetch('/api/permissions', { credentials: 'include' }))

      const [membersRes, lastSeenRes, onlineRes, permsRes] = await Promise.all(reqs)

      if (membersRes.ok) {
        setMembers(await membersRes.json())
      } else {
        const data = await membersRes.json().catch(() => ({}))
        setError(data.detail ?? `Error ${membersRes.status}`)
      }
      if (lastSeenRes.ok) setLastSeen(await lastSeenRes.json())
      if (onlineRes.ok) {
        const online: { id: string }[] = await onlineRes.json()
        setOnlineIds(new Set(online.map(u => u.id)))
      }
      if (permsRes?.ok) {
        const perms: { discord_id: string; role: string }[] = await permsRes.json()
        const map: Record<string, string> = {}
        perms.forEach(p => { map[p.discord_id] = p.role })
        setRoles(map)
      }
    } catch {
      setError(T.members.serverError)
    } finally {
      setLoading(false)
    }
  }, [canSeeRoles])

  useEffect(() => { fetchMembers(false) }, [fetchMembers])

  // Lig bandında bir başvuru cevaplanınca kartların rozeti de tazelensin.
  const refreshQuiet = useCallback(() => { void fetchMembers(false) }, [fetchMembers])

  const downloadXLSX = () => {
    const playtime = (m: Member) => {
      const p = m.wardogs_playtime
      const hours = (minutes: number) => Math.round(minutes / 6) / 10
      if (!p) return { [T.members.playtimeTotalColumn]: '', [T.members.playtimeFortnightColumn]: '' }
      if ('private' in p) return { [T.members.playtimeTotalColumn]: T.members.playtimePrivate, [T.members.playtimeFortnightColumn]: T.members.playtimePrivate }
      return {
        [T.members.playtimeTotalColumn]: hours(p.total),
        [T.members.playtimeFortnightColumn]: hours(p.two_weeks),
      }
    }
    const rows = members.map(m => (canSeeSteamIds
      ? { 'Nickname': m.display_name, 'Steam account': m.steam_name ?? '', 'SteamID64': m.steam_id ?? '', ...playtime(m) }
      : { 'Nickname': m.display_name, 'Steam account': m.steam_name ?? '', ...playtime(m) }
    ))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Members')
    XLSX.writeFile(wb, 'tll_members.xlsx')
  }

  // null = bu ölçütte değeri yok, sona gider.
  const sortValue = (m: Member): number | string | null => {
    const p = m.wardogs_playtime
    switch (sort.key) {
      case 'name': return m.display_name
      case 'lastSeen': return onlineIds.has(m.id) ? Infinity : (lastSeen[m.id] || null)
      case 'playtimeTotal': return p && !('private' in p) ? p.total : null
      case 'playtimeFortnight': return p && !('private' in p) ? p.two_weeks : null
      case 'clanJoined': return m.clan_joined_at || null
    }
  }
  const collator = new Intl.Collator(lang, { sensitivity: 'base', numeric: true })
  const compare = (a: Member, b: Member) => {
    const va = sortValue(a)
    const vb = sortValue(b)
    if (va === null || vb === null) {
      if (va === vb) return collator.compare(a.display_name, b.display_name)
      return va === null ? 1 : -1
    }
    const d = typeof va === 'string' ? collator.compare(va, vb as string) : (va as number) - (vb as number)
    // Eşitlikte ad karar verir, yönden bağımsız: aynı sürede iki kişi her
    // seferinde aynı sırada dursun.
    if (d === 0) return collator.compare(a.display_name, b.display_name)
    return sort.desc ? -d : d
  }

  const q = search.toLowerCase()
  const tll = members.filter(m => m.team === 'TLL' && (
    m.display_name.toLowerCase().includes(q) || m.username.toLowerCase().includes(q)
  )).sort(compare)
  const tlla = members.filter(m => m.team === 'TLLA' && (
    m.display_name.toLowerCase().includes(q) || m.username.toLowerCase().includes(q)
  )).sort(compare)

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={T.members.title} />
      <SteamLinkBanner />
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">

        {/* Kendi Steam bağlantın — *yönetim* görünümü: hesap, doğrulanmış
            durumu, yeniden bağla, kaldır. "Henüz bağlamadın" isteği yukarıdaki
            şerittir, bu kart yalnızca yönetilecek bir şey varken çıkar; ikisi
            asla aynı anda aynı şeyi söylemez.

            **Gerekçe metni buradan kalktı.** "Sunucu önceliği SteamID64'e
            tanımlanıyor…" bir ikna cümlesi ve yeri şerit; bu kartı gören zaten
            bağlamış biri, ona neden bağlaması gerektiğini anlatmak kartı iki
            kat uzatıp hiçbir şey söylemiyordu. Cümle şeritte duruyor.

            Kart tek satır: simge, hesap adı (profiline gider), durum, iki
            düğme. */}
        {me?.steam_linked && (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${panelCard} px-3 py-2`}>
          <SteamIcon size={14} />
          <a
            href={memberSteamProfileUrl(me.id)}
            target="_blank"
            rel="noreferrer"
            title={T.members.steamOpenProfile}
            className="truncate text-sm font-medium underline decoration-dotted underline-offset-2 transition-colors hover:text-primary"
          >
            {me.steam_name || T.members.steamNoName}
          </a>
          {me.steam_verified
            ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" aria-label={T.members.steamVerified} />
            : <span className="shrink-0 text-[10px] uppercase tracking-wider text-amber-400">
                {T.members.steamUnverified}
              </span>}

          <div className="ml-auto flex items-center gap-1.5">
            <a href="/api/steam/link">
              <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-[11px]">
                <SteamIcon size={12} />
                {me.steam_verified ? T.members.steamRelinkCta : T.members.steamLinkCta}
              </Button>
            </a>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-[11px] text-muted-foreground hover:text-destructive"
              onClick={() => void unlinkOwnSteam()}
            >
              {T.members.steamUnlinkCta}
            </Button>
          </div>
        </div>
        )}

        {steamMsg && (
          <p className="rounded-sm border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            {steamMsg}
          </p>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={T.members.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`${panelInput} pl-8`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label htmlFor="members-sort" className="text-xs text-muted-foreground">
              {T.members.sortLabel}
            </label>
            <select
              id="members-sort"
              value={sort.key}
              onChange={e => {
                const key = e.target.value as SortKey
                setSort({ key, desc: NATURAL_DESC[key] })
              }}
              className={`${panelInput} h-8 w-auto py-0 pr-7`}
            >
              {SORT_KEYS.map(k => (
                <option key={k} value={k}>{T.members.sortBy[k]}</option>
              ))}
            </select>
            <button
              type="button"
              className={panelIconButton}
              onClick={() => setSort({ ...sort, desc: !sort.desc })}
              title={sort.desc ? T.members.sortDesc : T.members.sortAsc}
              aria-label={sort.desc ? T.members.sortDesc : T.members.sortAsc}
            >
              {sort.desc
                ? <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                : <ArrowUpNarrowWide className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="ml-auto text-xs text-muted-foreground">
            {loading ? T.members.loading : T.members.count.replace('{n}', String(members.length))}
          </p>
          {/* Yeşil bir düğme panelde başka hiçbir yerde yok; renk Excel'in,
              bizim değil. Dosya biçimini söyleyen tek şey o renkti, artık
              etiketin kendisi söylüyor — düğme de bandın diline döndü. */}
          {canEdit && members.length > 0 && (
            <button className={panelGhostButton} onClick={downloadXLSX} disabled={loading}>
              <Download className="h-3 w-3" />
              {T.members.exportMembers}
            </button>
          )}
          <button className={panelGhostButton} onClick={() => fetchMembers(true)} disabled={loading}>
            <RefreshCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {T.members.refresh}
          </button>
        </div>

        {error && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {/* İki kadro iki banttır: TLL ve Akademi ayrı listelerdir, bir listenin
            iki başlığı değil. Band başlığı sayıyı da taşır, böylece kapalı
            ızgarada bile "kaç kişi" sorusunun cevabı üstte durur. */}
        {tll.length > 0 && (
          <Panel title={T.members.tllSection.replace('{n}', String(tll.length))}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tll.map(m => (
                <MemberCard
                  key={m.id}
                  member={m}
                  lastSeen={lastSeen[m.id] ?? 0}
                  isOnline={onlineIds.has(m.id)}
                  canSeeRoles={canSeeRoles}
                  canSeeSteamId={canSeeSteamIds}
                  memberRole={roles[m.id] ?? 'member'}
                  onManage={canEdit ? () => setManagingId(m.id) : undefined}
                />
              ))}
            </div>
          </Panel>
        )}

        {tlla.length > 0 && (
          <Panel title={T.members.tllaSection.replace('{n}', String(tlla.length))}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tlla.map(m => (
                <MemberCard
                  key={m.id}
                  member={m}
                  lastSeen={lastSeen[m.id] ?? 0}
                  isOnline={onlineIds.has(m.id)}
                  canSeeRoles={canSeeRoles}
                  canSeeSteamId={canSeeSteamIds}
                  memberRole={roles[m.id] ?? 'member'}
                  onManage={canEdit ? () => setManagingId(m.id) : undefined}
                />
              ))}
            </div>
          </Panel>
        )}

        {canEdit && <WdlRosterPanel onChanged={refreshQuiet} />}

        {canEdit && (
          <MemberManageDialog
            member={managing}
            onClose={() => setManagingId(null)}
            onSteamChanged={(id, change) => patchMember(id, change)}
            onNotesChanged={(id, count) => patchMember(id, { note_count: count })}
          />
        )}

        {!loading && members.length === 0 && !error && (
          <PanelEmpty>{T.members.noMembers}</PanelEmpty>
        )}
      </div>
    </div>
  )
}
